import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Users, FileText, Plus, ChevronRight, ChevronLeft, AlertCircle, CheckCircle, PauseCircle, XCircle, RefreshCw, LogOut, Shield, BarChart3, X, Loader2, MessageSquare, Heart, Vote } from 'lucide-react';
import {
  storage, OWL_LOGO,
  loadAllTenants, saveTenant, createTenant,
  getTenantUsersKey, getTenantRelsKey,
  DEFAULT_TENANT, PUBLIC_TENANT,
  getRoleLabel, STRIPE_CONFIG,
  getTenantBillingMethod,
} from '../shared.jsx';
import { SeatManagementPanel } from '../modules/billing/SeatManager.jsx';
import { STATUS_META, CancelTenantButton } from '../modules/billing/TenantStatusPanel.jsx';
import { createStripeAdapter } from '../modules/billing/adapters/StripeAdapter.js';
import LegalHoldLookupPanel from './LegalHoldLookupPanel.jsx';
import ModalDialog from './ModalDialog.jsx';
import LightReviewPanel from '../modules/freemium/light/LightReviewPanel.jsx';

// ─────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────

const stripeAdapter = createStripeAdapter({ cloudFunctionUrl: STRIPE_CONFIG.cloudFunctionUrl });

const PLAN_LABELS  = { free: 'Free', starter: 'Starter', professional: 'Professional', enterprise: 'Enterprise' };
const PLAN_COLORS  = {
  free:         'bg-stone-100 text-stone-600',
  starter:      'bg-blue-50 text-blue-700',
  professional: 'bg-indigo-50 text-indigo-700',
  enterprise:   'bg-amber-50 text-amber-700',
};
// STATUS_META imported from billing module (TenantStatusPanel.jsx)

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

async function loadTenantStats(tenantId) {
  try {
    const [users, rels, conflictKeys] = await Promise.all([
      storage.get(getTenantUsersKey(tenantId), true),
      storage.get(getTenantRelsKey(tenantId), true),
      storage.list('conflict:', true),
    ]);
    const userList     = Array.isArray(users) ? users : [];
    const relList      = Array.isArray(rels)  ? rels  : [];
    const allConflictKeys = Array.isArray(conflictKeys) ? conflictKeys : [];

    // Load conflicts that belong to this tenant
    const conflictData = await Promise.all(
      allConflictKeys.map(k => storage.get(k, true).catch(() => null))
    );
    const tenantConflicts = conflictData.filter(
      c => c && (c.tenantId === tenantId || (!c.tenantId && tenantId === DEFAULT_TENANT.id))
    );
    const activeConflicts   = tenantConflicts.filter(c => c.status !== 'resolved');
    const resolvedConflicts = tenantConflicts.filter(c => c.status === 'resolved');

    return {
      userList,
      userCount:         userList.length,
      activeSeats:       userList.filter(u => u.isActive !== false).length,
      relationshipCount: relList.length,
      totalConflicts:    tenantConflicts.length,
      activeConflicts:   activeConflicts.length,
      resolvedConflicts: resolvedConflicts.length,
    };
  } catch (e) {
    console.error('loadTenantStats error:', e);
    return { userList: [], userCount: 0, activeSeats: 0, relationshipCount: 0, totalConflicts: 0, activeConflicts: 0, resolvedConflicts: 0 };
  }
}

async function loadFreemiumStats(allTenants) {
  let siywSessions = 0, moodCheckins = 0, voteCount = 0, usersWithMood = 0, usersWithSiyw = 0;
  try {
    // Collect all user IDs across tenants
    const allUserIds = [];
    for (const tenant of allTenants) {
      if (!tenant?.id) continue;
      const usersSnap = await storage.get(getTenantUsersKey(tenant.id), true).catch(() => null);
      const users = Array.isArray(usersSnap) ? usersSnap : (usersSnap ? Object.values(usersSnap) : []);
      for (const u of users) {
        if (u?.id) allUserIds.push(u.id);
      }
    }

    // Load freemium data for each user in parallel
    const results = await Promise.all(allUserIds.map(async (userId) => {
      const [siyw, mood, votes] = await Promise.all([
        storage.get(`siyw-sessions:${userId}`, true).catch(() => null),
        storage.get(`mood:${userId}`, false).catch(() => null),
        storage.get(`vote:${userId}`, true).catch(() => null),
      ]);
      let sc = 0, mc = 0, vc = 0;
      if (siyw && typeof siyw === 'object') sc = Object.keys(siyw).length;
      if (mood) {
        const parsed = typeof mood === 'string' ? JSON.parse(mood) : mood;
        if (parsed && typeof parsed === 'object') mc = Object.keys(parsed).length;
      }
      if (votes && typeof votes === 'object') vc = Object.keys(votes).length;
      return { sc, mc, vc };
    }));

    for (const r of results) {
      siywSessions += r.sc;
      moodCheckins += r.mc;
      voteCount += r.vc;
      if (r.mc > 0) usersWithMood++;
      if (r.sc > 0) usersWithSiyw++;
    }
  } catch (e) {
    console.error('loadFreemiumStats error:', e);
  }
  return { siywSessions, moodCheckins, voteCount, usersWithMood, usersWithSiyw };
}

// ─────────────────────────────────────────────
//  Stat pill
// ─────────────────────────────────────────────
function StatPill({ label, value, sub }) {
  return (
    <div className="bg-stone-50 rounded-xl px-4 py-3 flex flex-col gap-0.5">
      <span className="text-xs text-stone-500 font-medium uppercase tracking-wide">{label}</span>
      <span className="text-2xl font-light text-stone-800">{value}</span>
      {sub && <span className="text-xs text-stone-500">{sub}</span>}
    </div>
  );
}

// ─────────────────────────────────────────────
//  Tenant drill-in panel (read-only summary)
// ─────────────────────────────────────────────
function TenantDetailPanel({ tenant, onBack, onStatusChange, onManageUsers, onTenantUpdated }) {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [showSeatPanel, setShowSeatPanel] = useState(false);
  const [showStripeConnect, setShowStripeConnect] = useState(false);
  const [stripeSubId, setStripeSubId] = useState('');
  const [stripeConnecting, setStripeConnecting] = useState(false);

  useEffect(() => {
    setLoading(true);
    loadTenantStats(tenant.id).then(s => { setStats(s); setLoading(false); });
  }, [tenant.id]);

  const statusMeta = STATUS_META[tenant.status] || STATUS_META.active;
  const StatusIcon = statusMeta.icon;

  const handleToggleSuspend = async () => {
    setSaving(true);
    const newStatus = tenant.status === 'suspended' ? 'active' : 'suspended';
    await onStatusChange(tenant.id, newStatus);
    setSaving(false);
  };

  const isSystemTenant = tenant.id === DEFAULT_TENANT.id || tenant.id === PUBLIC_TENANT.id;

  return (
    <div className="animate-[fadeSlideIn_0.2s_ease-out]">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-stone-500 hover:text-stone-800 text-sm mb-5 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" /> Back to tenants
      </button>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-stone-500" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-semibold text-stone-800">{tenant.name}</h2>
                {isSystemTenant && (
                  <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 font-medium">
                    System tenant
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${PLAN_COLORS[tenant.plan] || PLAN_COLORS.starter}`}>
                  {PLAN_LABELS[tenant.plan] || tenant.plan}
                </span>
                <span className={`flex items-center gap-1 text-xs font-medium ${statusMeta.color}`}>
                  <StatusIcon className="w-3.5 h-3.5" />
                  {statusMeta.label}
                </span>
                <span className="text-xs text-stone-500">Created {formatDate(tenant.createdAt)}</span>
                {getTenantBillingMethod(tenant) === 'manual' ? (
                  <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-600">Manual billing</span>
                ) : (
                  <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600">Stripe billing</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Manage Users button */}
            <button
              onClick={() => onManageUsers(tenant)}
              className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg border border-stone-200 text-stone-700 hover:bg-stone-50 transition-colors"
            >
              <Users className="w-4 h-4" />
              Manage users
            </button>

            {/* Suspend / reactivate — disabled for system tenants */}
            {!isSystemTenant && (
              <button
                onClick={handleToggleSuspend}
                disabled={saving}
                className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg border transition-colors flex-shrink-0 ${
                  tenant.status === 'suspended'
                    ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                    : 'border-amber-200 text-amber-700 hover:bg-amber-50'
                } disabled:opacity-50`}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PauseCircle className="w-4 h-4" />}
                {tenant.status === 'suspended' ? 'Reactivate' : 'Suspend'}
              </button>
            )}
          </div>
        </div>

        {/* Billing contact */}
        {tenant.billing?.contactEmail && (
          <div className="mt-4 pt-4 border-t border-stone-100 text-xs text-stone-500">
            Billing contact: <span className="text-stone-700">{tenant.billing.contactName || '—'}</span>
            {' '}·{' '}
            <a href={`mailto:${tenant.billing.contactEmail}`} className="text-amber-700 hover:underline">
              {tenant.billing.contactEmail}
            </a>
          </div>
        )}

        {/* Connect to Stripe (manual tenants only) */}
        {getTenantBillingMethod(tenant) === 'manual' && !tenant.stripeSubscriptionId && (
          <div className="mt-4 pt-4 border-t border-stone-100">
            {!showStripeConnect ? (
              <button
                onClick={() => setShowStripeConnect(true)}
                className="flex items-center gap-2 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
              >
                Connect to Stripe
              </button>
            ) : (
              <div className="space-y-2">
                <label htmlFor="stripe-subscription-id" className="block text-xs font-medium text-stone-600">Stripe Subscription ID</label>
                <div className="flex gap-2">
                  <input id="stripe-subscription-id"
                    type="text"
                    value={stripeSubId}
                    onChange={e => setStripeSubId(e.target.value)}
                    placeholder="sub_..."
                    className="flex-1 border border-stone-200 rounded-lg px-3 py-1.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
                  />
                  <button
                    onClick={async () => {
                      if (!stripeSubId.trim().startsWith('sub_')) { alert('Stripe Subscription IDs start with "sub_"'); return; }
                      setStripeConnecting(true);
                      try {
                        const allTenants = await loadAllTenants();
                        const updated = {
                          ...tenant,
                          stripeSubscriptionId: stripeSubId.trim(),
                          billing: { ...tenant.billing, method: 'stripe' },
                        };
                        await saveTenant(updated, allTenants);
                        if (onTenantUpdated) onTenantUpdated(updated);
                        setShowStripeConnect(false);
                        setStripeSubId('');
                      } catch (e) {
                        console.error('Connect to Stripe error:', e);
                        alert('Failed to connect. Please try again.');
                      }
                      setStripeConnecting(false);
                    }}
                    disabled={stripeConnecting || !stripeSubId.trim()}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {stripeConnecting ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    Link
                  </button>
                  <button
                    onClick={() => { setShowStripeConnect(false); setStripeSubId(''); }}
                    className="px-3 py-1.5 text-sm text-stone-500 hover:text-stone-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Cancel tenant */}
        <CancelTenantButton
          tenant={tenant}
          stripeAdapter={stripeAdapter}
          user={null}
          onCancelled={() => onTenantUpdated?.()}
          onError={(msg) => alert(msg)}
        />
      </div>

      {/* Stats grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-stone-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading stats…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            <StatPill
              label="Users"
              value={stats.userCount}
              sub={`${stats.activeSeats} active · ${tenant.maxSeats === 99999 ? '∞' : tenant.maxSeats} seats`}
            />
            <StatPill label="Active conflicts"   value={stats.activeConflicts} />
            <StatPill label="Resolved conflicts" value={stats.resolvedConflicts} />
            <StatPill label="Total conflicts"    value={stats.totalConflicts} />
            <StatPill label="Relationships"      value={stats.relationshipCount} />
            <StatPill
              label="Seat usage"
              value={tenant.maxSeats === 99999 ? '—' : `${Math.round((stats.activeSeats / tenant.maxSeats) * 100)}%`}
              sub={tenant.maxSeats === 99999 ? 'Unlimited' : `${stats.activeSeats} / ${tenant.maxSeats}`}
            />
          </div>

          {/* P5: Seat overage warning */}
          {tenant.maxSeats !== 99999 && stats.activeSeats >= tenant.maxSeats && (
            <div className="mb-4 flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <AlertCircle className="w-5 h-5 text-red-700 flex-shrink-0" />
              <p className="text-sm text-red-800 flex-1">
                Seat limit reached ({stats.activeSeats}/{tenant.maxSeats}). New users cannot be added until seats are increased or inactive users are removed.
              </p>
              <button
                onClick={() => setShowSeatPanel(true)}
                className="text-sm font-medium text-red-700 hover:text-red-900 underline flex-shrink-0"
              >
                Add seats
              </button>
            </div>
          )}

          {/* P5: Seat near-capacity warning (>= 90%) */}
          {tenant.maxSeats !== 99999 && stats.activeSeats < tenant.maxSeats && stats.activeSeats >= tenant.maxSeats * 0.9 && (
            <div className="mb-4 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0" />
              <p className="text-sm text-amber-800 flex-1">
                Approaching seat limit ({stats.activeSeats}/{tenant.maxSeats}). Consider adding seats before the limit is reached.
              </p>
            </div>
          )}

          {/* P5: Seat Management button */}
          {tenant.maxSeats !== 99999 && (
            <div className="mb-4">
              <button
                onClick={() => setShowSeatPanel(true)}
                className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg border border-purple-200 text-purple-700 hover:bg-purple-50 transition-colors"
              >
                <Users className="w-4 h-4" />
                Manage seats ({stats.activeSeats}/{tenant.maxSeats})
              </button>
            </div>
          )}

          {/* P5: Seat Management Panel */}
          {showSeatPanel && (
            <SeatManagementPanel
              tenant={tenant}
              activeSeats={stats.activeSeats}
              stripeAdapter={stripeAdapter}
              onClose={() => setShowSeatPanel(false)}
              onUpdated={(newMaxSeats) => {
                setShowSeatPanel(false);
                if (onTenantUpdated) onTenantUpdated({ ...tenant, maxSeats: newMaxSeats });
              }}
            />
          )}

          {/* User list */}
          {stats.userList.length > 0 && (
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm mb-4 overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-stone-100">
                <Users className="w-4 h-4 text-stone-500" />
                <span className="text-sm font-semibold text-stone-700">Users ({stats.userList.length})</span>
              </div>
              <div className="divide-y divide-stone-50">
                {stats.userList.map((u, i) => (
                  <div key={u.id || i} className="flex items-center justify-between px-5 py-3 hover:bg-stone-50 transition-colors">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-stone-800 truncate">{u.name || '—'}</div>
                      <div className="text-xs text-stone-500 truncate">{u.email}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      <span className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">
                        {getRoleLabel(u.role || 'mentee')}
                      </span>
                      {u.isActive === false && (
                        <span className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full">Inactive</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Settings summary */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5">
        <h3 className="text-sm font-semibold text-stone-700 mb-3">Tenant settings</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          {[
            ['Require mentor',       tenant.settings?.requireMentor ? 'Yes' : 'No'],
            ['Require NDA',          tenant.settings?.requireNDA    ? 'Yes' : 'No'],
            ['Self-service signup',  tenant.settings?.allowSelfServiceSignup ? 'Yes' : 'No'],
            ['Branding name',        tenant.settings?.brandingName || '—'],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between items-center py-1.5 border-b border-stone-50">
              <span className="text-stone-500">{k}</span>
              <span className="text-stone-800 font-medium">{v}</span>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateX(8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

// SeatManagementPanel imported from billing module

// ─────────────────────────────────────────────
//  Create Tenant modal
// ─────────────────────────────────────────────
function CreateTenantModal({ onClose, onCreated, user }) {
  const [name,           setName]           = useState('');
  const [plan,           setPlan]           = useState('starter');
  const [contactEmail,   setContactEmail]   = useState('');
  const [contactName,    setContactName]    = useState('');
  const [billingMethod,  setBillingMethod]  = useState('manual');
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState('');

  const handleCreate = async () => {
    if (!name.trim()) { setError('Tenant name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const tenant = createTenant({
        name: name.trim(),
        plan,
        billing: {
          contactEmail: contactEmail.trim(),
          contactName: contactName.trim(),
          method: billingMethod,
          managedBy: billingMethod === 'manual' ? (user?.id || null) : null,
        },
      });
      const allTenants = await loadAllTenants();
      await saveTenant(tenant, allTenants);
      onCreated(tenant);
    } catch (e) {
      console.error('Create tenant error:', e);
      setError('Failed to create tenant. Please try again.');
    }
    setSaving(false);
  };

  return (
    <ModalDialog onClose={onClose} labelledBy="create-tenant-title" className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-[fadeSlideIn_0.2s_ease-out]">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-stone-100">
          <h2 id="create-tenant-title" className="text-lg font-semibold text-stone-800">Create tenant</h2>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-700 transition-colors" aria-label="Close">
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label htmlFor="create-tenant-name" className="block text-xs font-medium text-stone-600 mb-1">Organization name *</label>
            <input id="create-tenant-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Acme Corporation"
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="create-tenant-plan" className="block text-xs font-medium text-stone-600 mb-1">Plan</label>
            <select id="create-tenant-plan"
              value={plan}
              onChange={e => setPlan(e.target.value)}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
            >
              <option value="free">Free (5 seats)</option>
              <option value="starter">Starter (25 seats)</option>
              <option value="professional">Professional (100 seats)</option>
              <option value="enterprise">Enterprise (999 seats)</option>
            </select>
          </div>

          <div>
            <label htmlFor="create-tenant-billing-method" className="block text-xs font-medium text-stone-600 mb-1">Billing method</label>
            <select id="create-tenant-billing-method"
              value={billingMethod}
              onChange={e => setBillingMethod(e.target.value)}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
            >
              <option value="manual">Manual (invoiced by Harmony)</option>
              <option value="stripe">Stripe (self-serve)</option>
            </select>
          </div>

          <div>
            <label htmlFor="create-tenant-contact-name" className="block text-xs font-medium text-stone-600 mb-1">Billing contact name</label>
            <input id="create-tenant-contact-name"
              type="text"
              value={contactName}
              onChange={e => setContactName(e.target.value)}
              placeholder="Jane CFO"
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="create-tenant-contact-email" className="block text-xs font-medium text-stone-600 mb-1">Billing contact email</label>
            <input id="create-tenant-contact-email"
              type="email"
              value={contactEmail}
              onChange={e => setContactEmail(e.target.value)}
              placeholder="billing@acme.com"
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-700 text-sm bg-red-50 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}
        </div>

        <div className="px-6 pb-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-stone-600 hover:text-stone-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-stone-900 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create tenant
          </button>
        </div>
      </div>
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </ModalDialog>
  );
}

// ─────────────────────────────────────────────
//  Tenant card (list item)
// ─────────────────────────────────────────────
function TenantCard({ tenant, onSelect }) {
  const statusMeta = STATUS_META[tenant.status] || STATUS_META.active;
  const StatusIcon = statusMeta.icon;
  const isSystem   = tenant.id === DEFAULT_TENANT.id || tenant.id === PUBLIC_TENANT.id;

  return (
    <button
      onClick={() => onSelect(tenant)}
      className="w-full text-left bg-white hover:bg-stone-50 rounded-xl border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow transition-all px-5 py-4 group"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5 text-stone-500" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-stone-800 truncate">{tenant.name}</span>
              {isSystem && (
                <span className="text-xs bg-amber-50 text-amber-700 border border-amber-100 rounded-full px-1.5 py-0.5 font-medium flex-shrink-0">
                  System
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${PLAN_COLORS[tenant.plan] || PLAN_COLORS.starter}`}>
                {PLAN_LABELS[tenant.plan] || tenant.plan}
              </span>
              <span className={`flex items-center gap-1 text-xs ${statusMeta.color}`}>
                <StatusIcon className="w-3 h-3" />
                {statusMeta.label}
              </span>
              <span className="text-xs text-stone-500">Created {formatDate(tenant.createdAt)}</span>
              {getTenantBillingMethod(tenant) === 'manual'
                ? <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-600">Manual</span>
                : <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600">Stripe</span>
              }
            </div>
          </div>
        </div>

        {/* Seat meter */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right hidden sm:block">
            <div className="text-sm font-medium text-stone-700">
              {tenant.maxSeats === 99999 ? '∞' : `${tenant.activeSeats} / ${tenant.maxSeats}`}
            </div>
            <div className="text-xs text-stone-500">seats</div>
          </div>
          <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-stone-500 transition-colors" />
        </div>
      </div>

      {/* Seat usage bar (non-unlimited tenants) */}
      {tenant.maxSeats !== 99999 && tenant.maxSeats > 0 && (
        <div className="mt-3 h-1 bg-stone-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-amber-400 rounded-full transition-all"
            style={{ width: `${Math.min(100, (tenant.activeSeats / tenant.maxSeats) * 100)}%` }}
          />
        </div>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────
//  Platform stats bar
// ─────────────────────────────────────────────
function PlatformStats({ tenants, platformStats }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 shadow-sm px-5 py-4">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="w-4 h-4 text-stone-500" />
        <span className="text-sm font-semibold text-stone-700">Platform stats</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatPill label="Tenants"          value={tenants.length} />
        <StatPill label="Total users"      value={platformStats.totalUsers}     />
        <StatPill label="Active conflicts" value={platformStats.activeConflicts} />
        <StatPill label="Resolved"         value={platformStats.resolvedConflicts} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  Freemium stats bar
// ─────────────────────────────────────────────
function FreemiumStats({ stats }) {
  if (!stats) return null;
  return (
    <div className="bg-white rounded-2xl border border-stone-200 shadow-sm px-5 py-4">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="w-4 h-4 text-purple-500" />
        <span className="text-sm font-semibold text-stone-700">Free Tools</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatPill label="SIYW Sessions" value={stats.siywSessions} sub={`${stats.usersWithSiyw} users`} />
        <StatPill label="Mood Check-ins" value={stats.moodCheckins} sub={`${stats.usersWithMood} users`} />
        <StatPill label="Votes" value={stats.voteCount} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  SupportDashboard — main export
// ─────────────────────────────────────────────
export default function SupportDashboard({ user, onLogout, onManageUsers }) {
  const [tenants,        setTenants]        = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showCreate,     setShowCreate]     = useState(false);
  const [loading,        setLoading]        = useState(true);
  const [refreshing,     setRefreshing]     = useState(false);
  const [platformStats,  setPlatformStats]  = useState({
    totalUsers: 0, activeConflicts: 0, resolvedConflicts: 0,
  });
  const [freemiumStats,  setFreemiumStats]  = useState(null);

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const allTenants = await loadAllTenants();
      setTenants(allTenants);

      // Aggregate platform stats across all tenants
      let totalUsers = 0, activeConflicts = 0, resolvedConflicts = 0;
      await Promise.all(allTenants.map(async t => {
        const s = await loadTenantStats(t.id);
        totalUsers        += s.userCount;
        activeConflicts   += s.activeConflicts;
        resolvedConflicts += s.resolvedConflicts;
      }));
      setPlatformStats({ totalUsers, activeConflicts, resolvedConflicts });

      // Load freemium tool stats
      loadFreemiumStats(allTenants).then(fs => setFreemiumStats(fs));
    } catch (e) {
      console.error('SupportDashboard loadData error:', e);
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleStatusChange = async (tenantId, newStatus) => {
    const allTenants = await loadAllTenants();
    const target = allTenants.find(t => t.id === tenantId);
    if (!target) return;
    const updated = { ...target, status: newStatus };
    await saveTenant(updated, allTenants);
    setTenants(prev => prev.map(t => t.id === tenantId ? updated : t));
    if (selectedTenant?.id === tenantId) setSelectedTenant(updated);
  };

  const handleTenantCreated = (newTenant) => {
    setTenants(prev => [...prev, newTenant]);
    setShowCreate(false);
    setSelectedTenant(newTenant);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 flex items-center justify-center">
              <img src={OWL_LOGO} alt="ArguMentor" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="text-xl font-light text-stone-800 tracking-tight">
                The ArguMentor
                <span className="ml-2 text-xs font-semibold uppercase tracking-widest text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                  Support
                </span>
              </h1>
              <p className="text-xs text-stone-500 font-light">Platform administration</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-sm text-stone-500">
              <Shield className="w-4 h-4 text-amber-700" />
              <span className="font-medium text-stone-700">{user.name}</span>
            </div>
            <button
              onClick={() => loadData(true)}
              disabled={refreshing}
              className="p-2 text-stone-500 hover:text-stone-700 hover:bg-stone-50 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onLogout}
              className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 hover:bg-stone-50 px-3 py-2 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-stone-500">
            <Loader2 className="w-6 h-6 animate-spin mr-3" />
            Loading platform data…
          </div>
        ) : selectedTenant ? (
          <TenantDetailPanel
            tenant={selectedTenant}
            onBack={() => setSelectedTenant(null)}
            onStatusChange={handleStatusChange}
            onManageUsers={onManageUsers}
            onTenantUpdated={(updatedTenant) => {
              setSelectedTenant(updatedTenant);
              setTenants(prev => prev.map(t => t.id === updatedTenant.id ? updatedTenant : t));
            }}
          />
        ) : (
          <div className="space-y-5">
            {/* Platform stats */}
            <PlatformStats tenants={tenants} platformStats={platformStats} />

            {/* Freemium tool stats */}
            <FreemiumStats stats={freemiumStats} />

            {/* Legal Hold Lookup — subpoena response tool */}
            <LegalHoldLookupPanel />

            {/* Say It Together review queue: release, end or dismiss */}
            <LightReviewPanel />

            {/* Tenant list header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-stone-500" />
                <h2 className="text-sm font-semibold text-stone-700">
                  Tenants
                  <span className="ml-2 text-stone-500 font-normal">({tenants.length})</span>
                </h2>
              </div>
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-stone-900 text-sm font-medium px-4 py-2 rounded-lg transition-colors shadow-sm"
              >
                <Plus className="w-4 h-4" />
                Create tenant
              </button>
            </div>

            {/* Tenant cards */}
            {tenants.length === 0 ? (
              <div className="text-center py-16 text-stone-500">
                <Building2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No tenants found. Create one to get started.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* System tenants first, then org tenants */}
                {[
                  ...tenants.filter(t => t.id === DEFAULT_TENANT.id || t.id === PUBLIC_TENANT.id),
                  ...tenants.filter(t => t.id !== DEFAULT_TENANT.id && t.id !== PUBLIC_TENANT.id),
                ].map(tenant => (
                  <TenantCard
                    key={tenant.id}
                    tenant={tenant}
                    onSelect={setSelectedTenant}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Create tenant modal */}
      {showCreate && (
        <CreateTenantModal
          onClose={() => setShowCreate(false)}
          onCreated={handleTenantCreated}
          user={user}
        />
      )}
    </div>
  );
}
